import { useCallback } from "react"
import { TimeRange, FormField } from "./types"
import { DateRangePicker as DateRangePicker_, RangeKeyDict } from "react-date-range"
import { endOfDay, startOfDay } from "date-fns"
import { useController, useFormContext } from "react-hook-form"
import { Tooltip } from "@mui/material"
import cs from "classnames"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import { formatDate } from "../Stats/TimelineStats/TimelineStats.utils"

import styles from "./Forms.module.scss"

import "react-date-range/dist/styles.css" // main style file
import "react-date-range/dist/theme/default.css" // theme css file
import { trackFormEvent } from "./utils"

export interface Props extends FormField {
  behindTooltip?: boolean
}

export const DateRangePicker = ({ behindTooltip, name, label, rules, tracking }: Props) => {
  const { control } = useFormContext()
  const controller = useController({ name, control, rules })

  const value = controller.field.value as TimeRange

  const handleDateSelect = useCallback(
    async (rangesByKey: RangeKeyDict) => {
      controller.field.onChange({
        start: startOfDay(rangesByKey.selection?.startDate || new Date()),
        end: endOfDay(rangesByKey.selection?.endDate || new Date()),
      })

      if (tracking?.shouldTrackValueChange) {
        await trackFormEvent("Form Value Provided", tracking, name, label)
      }
    },
    [controller.field, label, name, tracking]
  )

  const handleClearClick = useCallback(() => {
    controller.field.onChange({ start: null, end: null })
  }, [controller.field])

  const picker = (
    <div className="tmb-w-full tmb-relative">
      <button className={styles.clearButton} onClick={handleClearClick}>
        Clear
      </button>

      <DateRangePicker_
        onChange={handleDateSelect}
        ranges={[
          {
            startDate: value?.start || startOfDay(new Date()),
            endDate: value?.end || endOfDay(new Date()),
            key: "selection",
          },
        ]}
        direction="horizontal"
      />
    </div>
  )

  if (behindTooltip)
    return (
      <Tooltip classes={{ tooltip: cs(styles.dateRangePicker) }} title={picker} disableFocusListener leaveDelay={100}>
        <div className="tmb-flex tmb-flex-col tmb-cursor-pointer">
          <div className="tmb-flex tmb-items-center tmb-gap-2">
            <CalendarMonthIcon /> <span className={styles.dateRangePickerLabel}>{label}</span>
          </div>

          {value?.start || value?.end ? (
            <span className={styles.tooltipDatesLabel}>
              {value?.start ? formatDate(value?.start) : ""} - {value?.end ? formatDate(value?.end) : ""}
            </span>
          ) : null}
        </div>
      </Tooltip>
    )

  return picker
}

export default DateRangePicker
